import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Row,
  Col,
  Space,
  InputNumber,
  Typography,
  Input,
  Checkbox
} from 'antd';

const { Text } = Typography;
const { TextArea } = Input;

const AdditionalServicesSection = ({
  additionalServices,
  onToggleService,
  onUpdateServicePrice,
  customAdditionalServices = '',
  onCustomAdditionalServicesChange = () => {},
  readOnly = false
}) => {
  // Local state to manage immediate UI updates and prevent flickering
  const [localServices, setLocalServices] = useState({});
  const [localCustomServices, setLocalCustomServices] = useState('');

  // Initialize local state with props
  useEffect(() => {
    if (additionalServices && typeof additionalServices === 'object') {
      setLocalServices(additionalServices);
    }
  }, [additionalServices]);

  useEffect(() => {
    setLocalCustomServices(customAdditionalServices);
  }, [customAdditionalServices]);

  // Debounced update to parent - prevents rapid state changes
  const debouncedUpdatePrice = useCallback((serviceKey, value) => {
    const timeoutId = setTimeout(() => {
      if (typeof onUpdateServicePrice === 'function') {
        onUpdateServicePrice(serviceKey, value);
      }
    }, 100);
    
    return () => clearTimeout(timeoutId);
  }, [onUpdateServicePrice]);

  // Handle price changes with immediate local update
  const handlePriceChange = useCallback((serviceKey, value) => {
    // Immediate local update to prevent flickering
    setLocalServices(prev => ({
      ...prev,
      [serviceKey]: {
        ...prev[serviceKey],
        price: value || 0
      }
    }));

    // Debounced parent update
    debouncedUpdatePrice(serviceKey, value);
  }, [debouncedUpdatePrice]);

  // Handle toggle with immediate local update
  const handleToggleService = useCallback((serviceKey) => {
    // Immediate local update
    setLocalServices(prev => ({
      ...prev,
      [serviceKey]: {
        ...prev[serviceKey],
        selected: !prev[serviceKey]?.selected
      }
    }));

    // Immediate parent update for toggles (no debouncing needed)
    if (typeof onToggleService === 'function') {
      onToggleService(serviceKey);
    }
  }, [onToggleService]);

  // Handle custom services change
  const handleCustomServicesChange = useCallback((value) => {
    setLocalCustomServices(value);
    if (typeof onCustomAdditionalServicesChange === 'function') {
      onCustomAdditionalServicesChange(value);
    }
  }, [onCustomAdditionalServicesChange]);

  // Safety check for services data
  if (!localServices || typeof localServices !== 'object') {
    return (
      <Card title="Additional Services (For Reference Only)" style={{ marginBottom: 16 }}>
        <div>Loading additional services...</div>
      </Card>
    );
  }

  return (
    <Card title="Additional Services (For Reference Only)" style={{ marginBottom: 16 }}>
      <div style={{ marginBottom: 16 }}>
        <Text type="secondary">
          Rate of Additional Services - These are not included in the quotation total above and serve as reference for potential add-on services:
        </Text>
      </div>
      
      <Row gutter={[16, 16]} style={readOnly ? { opacity: 0.7 } : {}}>
        <Col span={12}>
          <Space>
            <Checkbox
              checked={localServices?.additionalTransport?.selected || false}
              onChange={() => !readOnly && handleToggleService('additionalTransport')}
              disabled={readOnly}
            />
            <span>Additional transport of scaffold - $</span>
            <InputNumber
              size="small"
              value={localServices?.additionalTransport?.price || 0}
              onChange={(value) => !readOnly && handlePriceChange('additionalTransport', value)}
              min={0}
              disabled={readOnly}
            />
          </Space>
        </Col>
        <Col span={12}>
          <Space>
            <Checkbox
              checked={localServices?.additionalInstallation?.selected || false}
              onChange={() => !readOnly && handleToggleService('additionalInstallation')}
              disabled={readOnly}
            />
            <span>Additional installation / dismantling - $</span>
            <InputNumber
              size="small"
              value={localServices?.additionalInstallation?.price || 0}
              onChange={(value) => !readOnly && handlePriceChange('additionalInstallation', value)}
              min={0}
              disabled={readOnly}
            />
          </Space>
        </Col>
        <Col span={12}>
          <Space>
            <Checkbox
              checked={localServices?.stairsInstallation?.selected || false}
              onChange={() => !readOnly && handleToggleService('stairsInstallation')}
              disabled={readOnly}
            />
            <span>Installation of scaffold on stairs - $</span>
            <InputNumber
              size="small"
              value={localServices?.stairsInstallation?.price || 0}
              onChange={(value) => !readOnly && handlePriceChange('stairsInstallation', value)}
              min={0}
              disabled={readOnly}
            />
          </Space>
        </Col>
        <Col span={12}>
          <Space>
            <Checkbox
              checked={localServices?.stairsLoading?.selected || false}
              onChange={() => !readOnly && handleToggleService('stairsLoading')}
              disabled={readOnly}
            />
            <span>Loading & unloading of scaffold via stairs - from $</span>
            <InputNumber
              size="small"
              value={localServices?.stairsLoading?.price || 0}
              onChange={(value) => !readOnly && handlePriceChange('stairsLoading', value)}
              min={0}
              disabled={readOnly}
            />
          </Space>
        </Col>
        <Col span={12}>
          <Space>
            <Checkbox
              checked={localServices?.weeklyInspection?.selected || false}
              onChange={() => !readOnly && handleToggleService('weeklyInspection')}
              disabled={readOnly}
            />
            <span>Weekly Scaffold inspection and endorsement - $</span>
            <InputNumber
              size="small"
              value={localServices?.weeklyInspection?.price || 0}
              onChange={(value) => !readOnly && handlePriceChange('weeklyInspection', value)}
              min={0}
              disabled={readOnly}
            />
            <span> / Project</span>
          </Space>
        </Col>
      </Row>

      {/* Optional custom additional services text box */}
      <div style={{ marginTop: 16 }}>
        <Text strong>Other Additional Services (Optional):</Text>
        <TextArea
          rows={3}
          placeholder={readOnly ? "No additional services specified" : "Enter any other additional services if needed..."}
          value={localCustomServices}
          onChange={(e) => !readOnly && handleCustomServicesChange(e.target.value)}
          style={{ marginTop: 8, ...(readOnly ? { opacity: 0.7 } : {}) }}
          disabled={readOnly}
        />
      </div>

      <div style={{ marginTop: 16, padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '6px' }}>
        <Text type="secondary" style={{ fontStyle: 'italic' }}>
          Note: The services selected above are for reference only and are not included in the main quotation total. 
          They can be added as separate line items if required.
        </Text>
      </div>
    </Card>
  );
};

export default AdditionalServicesSection;
